import { create } from 'zustand';
import {
  loginUser,
  registerUser,
  logoutUser,
  refreshUserSession,
  AuthUser,
  RegisterPayload,
  activateUser,
  deleteUserProfile,
  changePassword
} from '../api/auth.api';
import axiosClient from '../api/axiosClient';
import {
  fetchAllUsersList,
  AdminUserRecord,
  toggleUserStatus
} from '../api/users.api';

interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  initializing: boolean;
  usersList: AdminUserRecord[];

  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  initAuth: () => Promise<void>;
  getUsersDirectory: () => Promise<void>;
  changeUserStatus: (userId: string, shouldActivate: boolean) => Promise<void>;
  activate: (token: string) => Promise<void>;
  purgeAccount: () => Promise<void>;
  changeUserPassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  accessToken: null,
  user: null,
  loading: false,
  error: null,
  initializing: true,
  usersList: [],

  async getUsersDirectory() {
    set({ loading: true, error: null });
    try {
      const usersData = await fetchAllUsersList();
      set({ usersList: usersData, loading: false });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'Failed to load user directory database profiles.';
      set({ loading: false, error: message });
      throw err;
    }
  },

  async changeUserStatus(userId, shouldActivate) {
    try {
      const updatedUser = await toggleUserStatus(userId, shouldActivate);
      set((state) => ({
        usersList: state.usersList.map((user) => {
          if (user.id === userId) {
            return {
              ...user,
              isActive: Boolean(updatedUser.isActive)
            };
          }
          return user;
        })
      }));
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'Failed to modify account authorization settings.';
      set({ error: message });
      throw err;
    }
  },

  async login(email, password) {
    set({ loading: true, error: null });
    try {
      const data = await loginUser({ email, password });
      set({
        accessToken: data.accessToken,
        user: data.user,
        isAuthenticated: true,
        loading: false,
        error: null
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'Unable to sign in. Please check your credentials.';
      set({
        loading: false,
        error: message,
        isAuthenticated: false,
        accessToken: null,
        user: null
      });
      throw err;
    }
  },

  purgeAccount: async () => {
    set({ loading: true, error: null });
    try {
      await deleteUserProfile();
      // Wipe client memory completely
      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        loading: false,
        error: null
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to complete profile deletion.';
      set({ loading: false, error: message });
      throw err;
    }
  },

  changeUserPassword: async (currentPassword, newPassword) => {
    set({ loading: true, error: null });
    try {
      await changePassword(currentPassword, newPassword);
      set({ loading: false, error: null });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to change password.';
      set({ loading: false, error: message });
      throw err;
    }
  },

  async register(payload) {
    set({ loading: true, error: null });
    try {
      // 🚀 Autofill backend mandatory string validations gracefully if left optional on client
      const processedPayload: RegisterPayload = {
        firstName: payload.firstName?.trim() || 'N/A',
        lastName: payload.lastName?.trim() || 'N/A',
        email: payload.email,
        password: payload.password,
        acceptedTerms: payload.acceptedTerms
      };
      await registerUser(processedPayload);
      set({ loading: false, error: null });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Unable to register. Please try again.';
      set({ loading: false, error: message });
      throw err;
    }
  },

  activate: async (token: string) => {
    set({ loading: true, error: null });
    try {
      const data = await activateUser(token);
      set({
        accessToken: data.accessToken,
        user: data.user,
        isAuthenticated: true,
        loading: false,
        error: null
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'Activation token is invalid or has expired.';
      set({
        loading: false,
        error: message,
        isAuthenticated: false,
        accessToken: null,
        user: null
      });
      throw err;
    }
  },

  async logout() {
    try {
      await logoutUser();
    } catch {
      // ignore
    }
    set({
      isAuthenticated: false,
      accessToken: null,
      user: null,
      loading: false,
      error: null
    });
  },

  async refreshSession() {
    try {
      const data = await refreshUserSession();
      set({
        accessToken: data.accessToken,
        user: data.user,
        isAuthenticated: true,
        error: null
      });
    } catch (err) {
      set({ isAuthenticated: false, accessToken: null, user: null });
      throw err;
    }
  },

  async initAuth() {
    try {
      await get().refreshSession();
    } catch {
      // ignore
    } finally {
      set({ initializing: false });
    }
  }
}));

// Request Interceptor
axiosClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
      (config.headers as any)['Authorization'] = `Bearer ${accessToken}`;
    }
  }
  return config;
});

// Response Interceptor & Concurrency Queue Tracker
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/activate') ||
      originalRequest.url?.includes('/auth/refresh');

    if (isAuthEndpoint) return Promise.reject(error);

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
            return axiosClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        await useAuthStore.getState().refreshSession();
        const newAccessToken = useAuthStore.getState().accessToken;

        if (newAccessToken) {
          originalRequest.headers.set(
            'Authorization',
            `Bearer ${newAccessToken}`
          );
          processQueue(null, newAccessToken);
          isRefreshing = false;
          return await axiosClient(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        await useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
